import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Button,
    Form,
    Row,
    Col,
    Pagination,
    Modal,
    Card,
    Badge
} from 'react-bootstrap';
import { agentService } from '../../services/agentService';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { countryCodes } from '../../utils/countryCodes';
import CountryCodeSelect from '../common/CountryCodeSelect';

const UserWalletDetails = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [userWallets, setUserWallets] = useState({ bankCards: [], cryptoWallets: [] });
    const [walletLoading, setWalletLoading] = useState(false);
    const [selectedQR, setSelectedQR] = useState(null);

    const [filters, setFilters] = useState({
        userId: '',
        mobileNumber: '',
        countryCode: ''
    });

    const fetchUsers = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const queryParams = {
                page,
                limit: 10,
                userId: filters.userId,
                mobileNumber: (filters.countryCode || '') + (filters.mobileNumber || '')
            };
            const response = await agentService.getUsers(queryParams);
            if (response.success) {
                setUsers(response.users);
                setTotalPages(response.pagination.pages);
                setCurrentPage(response.pagination.page);
            }
        } catch (error) {
            toast.error(t('error_fetch_users'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => {
        fetchUsers(1);
    }, [fetchUsers]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ userId: '', mobileNumber: '', countryCode: '' });
    };

    const handlePageChange = (page) => {
        fetchUsers(page);
    };

    const openWalletDetails = async (user) => {
        setSelectedUser(user);
        setShowWalletModal(true);
        setWalletLoading(true);
        try {
            const response = await agentService.getUserWallets(user.id);
            if (response.success) {
                setUserWallets({
                    bankCards: (response.bankCards || []).filter(card => card.status === 'active'),
                    cryptoWallets: (response.cryptoWallets || []).filter(wallet => wallet.status === 'active')
                });
            }
        } catch (error) {
            toast.error(t('error_fetch_wallet_details', 'Failed to fetch wallet details'));
        } finally {
            setWalletLoading(false);
        }
    };

    return (
        <div className="majestic-page-wrapper">

            {/* Header */}
            <h4 className="mb-4 text-white">{t('menu_user_wallet_details')}</h4>

            {/* Filter Section */}
            <div className="majestic-card filter-bar mb-4" style={{ overflow: 'visible', position: 'relative', zIndex: 100 }}>
                <Row className="g-3 w-100">
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <Form.Control
                                id="userId"
                                type="text"
                                name="userId"
                                placeholder={t('user_id')}
                                value={filters.userId}
                                onChange={handleFilterChange}
                                style={{ minWidth: '150px' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <CountryCodeSelect
                                value={filters.countryCode}
                                onChange={(code) => handleFilterChange({ target: { name: 'countryCode', value: code } })}
                                placeholder={t('all_countries')}
                                className="me-2"
                            />
                            <Form.Control
                                id="mobileNumber"
                                type="text"
                                name="mobileNumber"
                                placeholder={t('mobile_number')}
                                value={filters.mobileNumber}
                                onChange={handleFilterChange}
                                style={{ minWidth: '150px' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto" className="ms-auto">
                        <div className="d-flex gap-2">
                            <Button variant="primary" size="sm" className="px-3" onClick={() => fetchUsers(1)}>{t('query')}</Button>
                            <Button variant="secondary" size="sm" className="px-3" onClick={clearFilters}>{t('reset')}</Button>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Data Table */}
            <div className="majestic-card p-0 majestic-table-container shadow-sm">
                <Table hover className="border-0 mb-0">
                    <thead>
                        <tr>
                            <th>{t('user_id')}</th>
                            <th>{t('username')}</th>
                            <th>{t('user_nickname')}</th>
                            <th>{t('mobile_number')}</th>
                            <th>{t('credit_score')}</th>
                            <th>{t('registration_time')}</th>
                            <th className="text-end">{t('operate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-5">{t('no_data')}</td></tr>
                        ) : (
                            users.map(user => (
                                <tr key={user.id}>
                                    <td>{user.id.slice(-8).toUpperCase()}</td>
                                    <td style={{ color: 'var(--text-primary)' }}>{user.username}</td>
                                    <td>{user.email.split('@')[0]}</td>
                                    <td>
                                        {(() => {
                                            if (!user.mobileNumber) return '-';
                                            const matchedCode = countryCodes.find(c => user.mobileNumber.startsWith(c.code));
                                            if (matchedCode) {
                                                const number = user.mobileNumber.replace(matchedCode.code, '');
                                                return `${matchedCode.flag} ${matchedCode.code} ${number}`;
                                            }
                                            return user.mobileNumber;
                                        })()}
                                    </td>
                                    <td>{user.creditScore}</td>
                                    <td>{new Date(user.createdAt).toLocaleString('en-GB', { hour12: false })}</td>
                                    <td className="text-end">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => openWalletDetails(user)}
                                        >
                                            {t('view_more')}
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-end mt-3">
                <Pagination size="sm">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => (
                        <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => handlePageChange(i + 1)}>
                            {i + 1}
                        </Pagination.Item>
                    ))}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
            </div>

            {/* Wallet Details Modal */}
            <Modal
                show={showWalletModal}
                onHide={() => setShowWalletModal(false)}
                size="xl"
                centered
                contentClassName="bg-transparent border-0"
            >
                <div className="majestic-modal-content" style={{
                    background: 'linear-gradient(145deg, #1a1a1a 0%, #0d0d0d 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                }}>
                    <Modal.Header closeButton className="border-bottom border-warning border-opacity-25" style={{ background: 'transparent' }}>
                        <Modal.Title className="text-white d-flex align-items-center">
                            <span className="majestic-icon-box me-3" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)', padding: '8px', borderRadius: '8px' }}>
                                <i className="bi bi-wallet2 text-warning"></i>
                            </span>
                            <div>
                                <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>{t('menu_user_wallet_details')}</div>
                                <div className="fw-bold" style={{ color: '#D4AF37' }}>{selectedUser?.username}</div>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4" style={{ background: 'transparent' }}>
                        {walletLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-warning" role="status"></div>
                                <div className="mt-2 text-warning">{t('loading_data')}</div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-4">
                                {/* Bank Cards */}
                                <Card className="border-0 bg-transparent">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0 text-white d-flex align-items-center">
                                            <i className="bi bi-credit-card text-warning me-2 fs-5"></i>
                                            {t('bank_accounts')}
                                        </h6>
                                        <Badge bg="transparent" className="border border-warning text-warning px-3 py-2 rounded-pill">
                                            {userWallets.bankCards.length} {t('cards', 'Cards')}
                                        </Badge>
                                    </div>
                                    <div className="majestic-table-wrapper rounded-3" style={{ border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                        {userWallets.bankCards.length === 0 ? (
                                            <div className="text-center text-secondary py-4 small">{t('no_bank_cards')}</div>
                                        ) : (
                                            <Table hover variant="dark" className="mb-0 bg-transparent align-middle">
                                                <thead style={{ background: 'rgba(0,0,0,0.4)' }}>
                                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                                        <th className="py-3 ps-4 text-secondary text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>{t('bank_name')}</th>
                                                        <th className="py-3 text-secondary text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>{t('bank_account_number', 'Bank Account Number')}</th>
                                                        <th className="py-3 text-secondary text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>{t('card_number', 'Card Number')}</th>
                                                        <th className="py-3 text-secondary text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>{t('cardholder_name', 'Cardholder Name')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {userWallets.bankCards.map((card, idx) => (
                                                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                            <td className="ps-4 py-3 fw-bold text-white">{card.bankName}</td>
                                                            <td className="py-3" style={{ color: '#D4AF37' }}>{card.bankAccountNumber || '-'}</td>
                                                            <td className="py-3" style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>{card.accountNumber}</td>
                                                            <td className="py-3 text-white">{card.accountName}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </div>
                                </Card>

                                {/* Crypto Wallets */}
                                <Card className="border-0 bg-transparent">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0 text-white d-flex align-items-center">
                                            <i className="bi bi-currency-bitcoin text-warning me-2 fs-5"></i>
                                            {t('crypto_wallets_title')}
                                        </h6>
                                        <Badge bg="transparent" className="border border-warning text-warning px-3 py-2 rounded-pill">
                                            {userWallets.cryptoWallets.length} {t('wallets', 'Wallets')}
                                        </Badge>
                                    </div>
                                    <div className="majestic-table-wrapper rounded-3" style={{ border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                        {userWallets.cryptoWallets.length === 0 ? (
                                            <div className="text-center text-secondary py-4 small">{t('no_crypto_wallets')}</div>
                                        ) : (
                                            <Table hover variant="dark" className="mb-0 bg-transparent align-middle">
                                                <thead style={{ background: 'rgba(0,0,0,0.4)' }}>
                                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                                        <th className="py-3 ps-4 text-secondary text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>{t('network')}</th>
                                                        <th className="py-3 text-secondary text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>{t('wallet_address')}</th>
                                                        <th className="py-3 text-secondary text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>{t('wallet_qr_code', 'QR Code')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {userWallets.cryptoWallets.map((wallet, idx) => (
                                                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                            <td className="ps-4 py-3 fw-bold text-white">{wallet.network}</td>
                                                            <td className="py-3 text-break text-white-50" style={{ maxWidth: '250px', fontFamily: 'monospace' }}>{wallet.walletAddress}</td>
                                                            <td className="py-3">
                                                                {wallet.qrCodeImage ? (
                                                                    <div
                                                                        className="d-inline-block p-1 bg-white rounded-2 cursor-pointer transition-transform"
                                                                        style={{ transition: 'transform 0.2s' }}
                                                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                                        onClick={() => setSelectedQR(wallet.qrCodeImage)}
                                                                    >
                                                                        <img
                                                                            src={wallet.qrCodeImage}
                                                                            alt="QR"
                                                                            style={{
                                                                                width: '32px',
                                                                                height: '32px',
                                                                                objectFit: 'contain'
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-secondary opacity-50">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </div>
                                </Card>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-top border-secondary border-opacity-25" style={{ background: 'transparent' }}>
                        <Button variant="outline-light" className="px-4 rounded-pill" onClick={() => setShowWalletModal(false)}>{t('close')}</Button>
                    </Modal.Footer>
                </div>
            </Modal>

            {/* QR Large View Modal */}
            {selectedQR && (
                <div
                    className="qr-modal-overlay"
                    onClick={() => setSelectedQR(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        animation: 'fadeIn 0.3s ease'
                    }}
                >
                    <div
                        className="qr-modal-content"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'linear-gradient(145deg, #1a1a1a, #0d0d0d)',
                            padding: '25px',
                            borderRadius: '24px',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            boxShadow: '0 0 50px rgba(212, 175, 55, 0.2)',
                            maxWidth: '90%',
                            width: '320px',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            textAlign: 'center',
                            position: 'relative',
                            zIndex: 10000
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '24px',
                                zIndex: 10
                            }}
                            onClick={() => setSelectedQR(null)}
                        >
                            <i className="bi bi-x-circle-fill opacity-75"></i>
                        </div>

                        <div className="mb-3">
                            <i className="bi bi-qr-code text-warning fs-3"></i>
                            <h5 className="text-white mt-2 fw-bold">{t('wallet_qr_code', 'Wallet Address QR Code')}</h5>
                        </div>

                        <div
                            style={{
                                background: '#fff',
                                padding: '15px',
                                borderRadius: '16px',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.5)'
                            }}
                        >
                            <img
                                src={selectedQR}
                                alt="QR Large"
                                style={{
                                    width: '100%',
                                    maxHeight: '50vh',
                                    height: 'auto',
                                    display: 'block',
                                    borderRadius: '8px',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>

                        <button
                            className="btn btn-warning w-100 mt-4 py-2 fw-bold"
                            style={{ borderRadius: '12px', background: '#D4AF37', border: 'none', color: '#000' }}
                            onClick={() => setSelectedQR(null)}
                        >
                            {t('close', 'Close')}
                        </button>
                    </div>
                </div>
            )}

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .qr-modal-content {
                    animation: slideUpModal 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                @keyframes slideUpModal {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                `}
            </style>
        </div>
    );
};

export default UserWalletDetails;
